/**
 * Data and verdicts behind the dev environment picker (`EnvironmentSwitcher`).
 *
 * The picker asks two questions, and they are kept apart here: WHAT can I switch to
 * (the databases and the chatbots, from two independent sources, offered as two
 * independent choices) and IS WHAT I AM ON COHERENT (the chatbot's self-report,
 * compared against the app's database). Both are react-query calls, so the async
 * lifecycle — loading, error, abort on unmount, the refetch behind the Refresh
 * button — belongs to the query cache rather than to a pile of component state.
 *
 * Everything below the hooks is pure: given a health report it says what the picker
 * shows, which is the part worth testing without a DOM.
 */

import { useQuery } from "@tanstack/react-query";
import { Check, RefreshCw, TriangleAlert, type LucideIcon } from "lucide-react";
import {
  getActiveSupabaseUrl,
  normalizeChatbotUrl,
  readSupabaseOverride,
  resolveChatbotBaseUrl,
  supabaseProjectRef,
} from "@/lib/chatbotBaseUrl";
import {
  describePairing,
  DEVELOP_CHATBOT,
  fetchBranches,
  fetchChatbotHealth,
  fetchSandboxes,
  HttpError,
  listChatbots,
  listDatabases,
  type ChatbotHealth,
  type ChatbotTarget,
  type DatabaseTarget,
  type Environment,
  type Pairing,
} from "@/lib/sandboxEnvironments";

/** Long enough that reopening the dialog reuses the answer, short enough to stay honest. */
const STALE_MS = 60_000;

export type HealthState =
  | { status: "checking" }
  | { status: "ok"; health: ChatbotHealth }
  /** 404 — this chatbot predates the health endpoint. */
  | { status: "absent" }
  | { status: "unreachable"; message: string };

/** One line of prose about the pairing, ready to render. */
export interface Verdict {
  text: string;
  className: string;
  icon: LucideIcon;
  spin?: boolean;
  /** An alarm the user must not miss — the app-wide trigger button goes red. */
  broken: boolean;
}

export interface EnvironmentsQuery {
  /** One row per database, never duplicated by however many chatbots name it. */
  databases: DatabaseTarget[];
  /** Every chatbot any of those databases can be paired with. */
  chatbots: ChatbotTarget[];
  isLoading: boolean;
  branchError: string | null;
  sandboxError: string | null;
  refresh: () => void;
}

export interface PairingView {
  chatbotUrl: string | undefined;
  appRef: string | null;
  health: HealthState;
  verdict: Verdict;
  /**
   * Whether the chatbot in use resolves its database from the caller's token.
   * Undefined when it is unreachable or predates the field — "not known", never false.
   *
   * Routing is switched on by cluster secrets shared by every chatbot in the
   * environment, so the one we can see answers for its siblings. That is what lets a
   * row being OFFERED be judged without probing each chatbot separately.
   */
  routesPerToken: boolean | undefined;
}

function messageOf(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

/** A query's failure as display text, or null when it has not failed. */
export function errorText(error: unknown): string | null {
  return error ? messageOf(error) : null;
}

/**
 * The two axes, from two INDEPENDENT sources: the sandbox router not existing yet must
 * not hide the Supabase branch list. Each source keeps its own error and contributes an
 * empty list, so a missing registry costs the chatbot choices and nothing else — the
 * databases still list, and the develop chatbot is still offered for every one of them.
 */
export function useEnvironments(enabled: boolean): EnvironmentsQuery {
  const branches = useQuery({
    queryKey: ["sandbox-branches"],
    queryFn: ({ signal }) => fetchBranches(signal),
    enabled,
    retry: false,
    staleTime: STALE_MS,
  });
  const sandboxes = useQuery({
    queryKey: ["sandbox-chatbots"],
    queryFn: ({ signal }) => fetchSandboxes(signal),
    enabled,
    retry: false,
    staleTime: STALE_MS,
  });

  return {
    databases: listDatabases(branches.data || []),
    chatbots: listChatbots(sandboxes.data || []),
    isLoading: branches.isFetching || sandboxes.isFetching,
    branchError: errorText(branches.error),
    sandboxError: errorText(sandboxes.error),
    refresh: () => {
      void branches.refetch();
      void sandboxes.refetch();
    },
  };
}

/** What the probe of `baseUrl` has established so far. */
export function healthStateOf(
  baseUrl: string | undefined,
  data: ChatbotHealth | undefined,
  error: unknown
): HealthState {
  if (!baseUrl) return { status: "unreachable", message: "No chatbot URL is configured." };
  // A 404 is not a failure: this chatbot simply predates /health, so the pairing is
  // unchecked rather than broken.
  if (error instanceof HttpError && error.status === 404) return { status: "absent" };
  if (error) return { status: "unreachable", message: messageOf(error) };
  if (data) return { status: "ok", health: data };
  return { status: "checking" };
}

/**
 * Probe the chatbot the app is ACTUALLY talking to, never a row the user is only looking
 * at: the verdict decides the colour of an app-wide trigger button, so it has to describe
 * what is in use. Probing from mount (not only when the dialog opens) is what lets the
 * trigger warn about a mismatch you would otherwise have to go looking for.
 *
 * Keyed on the URL, so an applied override re-probes and react-query aborts the request
 * for the URL we have left behind.
 */
export function useChatbotPairing(enabled: boolean): PairingView {
  const chatbotUrl = resolveChatbotBaseUrl();
  const appRef = supabaseProjectRef(getActiveSupabaseUrl());

  const { data, error } = useQuery({
    // Keyed on the URL although the probe resolves it again: the key is what makes an
    // applied override re-probe and abort the request for the URL we have left behind.
    queryKey: ["chatbot-health", chatbotUrl],
    queryFn: ({ signal }) => fetchChatbotHealth(signal),
    enabled: enabled && Boolean(chatbotUrl),
    retry: false,
    staleTime: STALE_MS,
  });

  const health = healthStateOf(chatbotUrl, data, error);
  return {
    chatbotUrl,
    appRef,
    health,
    verdict: verdictOf(health, appRef),
    routesPerToken: health.status === "ok" ? health.health.routes_per_token : undefined,
  };
}

/** One pairing as a line of prose — for the pair in use, and for one being offered. */
export function pairingVerdict(pairing: Pairing): Verdict {
  switch (pairing.kind) {
    case "match":
      return {
        text: `same database (${pairing.ref})`,
        className: "text-emerald-600",
        icon: Check,
        broken: false,
      };
    case "mismatch":
      return {
        text: `DIFFERENT DATABASE — this chatbot is pinned to ${pairing.chatbotRef}, the app is on ${pairing.appRef}. Its answers do not describe what you are looking at.`,
        className: "font-medium text-destructive",
        icon: TriangleAlert,
        broken: true,
      };
    case "per-request":
      return {
        text: `routes from your token — this chatbot boots on ${pairing.chatbotRef} but is not pinned to it, so it answers from ${pairing.appRef}.`,
        className: "text-muted-foreground",
        icon: Check,
        broken: false,
      };
    case "self-mismatch":
      return {
        text: `MISCONFIGURED CHATBOT — its API and pooler settings name different projects (it reports ${pairing.chatbotRef}).`,
        className: "font-medium text-destructive",
        icon: TriangleAlert,
        broken: true,
      };
    case "unknown":
      return {
        text: pairing.reason,
        className: "text-amber-600",
        icon: TriangleAlert,
        broken: false,
      };
  }
}

/** The one line the picker shows about the chatbot in use, and whether it is an alarm. */
export function verdictOf(state: HealthState, appRef: string | null): Verdict {
  switch (state.status) {
    case "checking":
      return {
        text: "checking…",
        className: "text-muted-foreground",
        icon: RefreshCw,
        spin: true,
        broken: false,
      };
    case "unreachable":
      return {
        text: `unreachable (${state.message})`,
        className: "text-destructive",
        icon: TriangleAlert,
        broken: false,
      };
    case "absent":
      return {
        text: "this chatbot predates /health, so the pairing cannot be verified.",
        className: "text-amber-600",
        icon: TriangleAlert,
        broken: false,
      };
    case "ok":
      return pairingVerdict(describePairing(state.health, appRef));
  }
}

/** The chatbot's own build line, or null when it reports nothing worth showing. */
export function buildLine(state: HealthState): string | null {
  if (state.status !== "ok" || !state.health.environment) return null;
  const { environment, sandbox, sha } = state.health;
  let line = `environment: ${environment}`;
  if (sandbox) line += ` · sandbox ${sandbox}`;
  if (sha) line += ` · ${sha.slice(0, 7)}`;
  return line;
}

/** What the trigger button says the app's database is. */
export function activeBranchLabel(): string {
  const override = readSupabaseOverride();
  return override ? override.branchName : "Default environment";
}

/** Display host for the chatbot in use — `host/path`, or why there is none. */
export function chatbotHostLabel(url: string | undefined): string {
  if (!url) return "not configured";
  try {
    const parsed = new URL(url);
    return `${parsed.host}${parsed.pathname}`.replace(/\/+$/, "");
  } catch {
    return "not configured";
  }
}

/** True only when BOTH halves already point where this row says. */
export function isCurrentEnvironment(env: Environment, appRef: string | null): boolean {
  if (env.ref !== appRef) return false;
  // The RESOLVED chatbot, not just the override: with no override the app is on its
  // build-time chatbot, which is the develop one every branch row offers. Resolving it
  // also drops an override this build would refuse to honour, so a hand-written
  // localStorage value cannot make a row claim to be the one in use.
  return env.chatbot.url === normalizeChatbotUrl(resolveChatbotBaseUrl() || "");
}

const STATUS_DOT_CLASS: Record<string, string> = {
  ACTIVE: "bg-green-500",
  FUNCTIONS_DEPLOYED: "bg-green-500",
  CREATING_PROJECT: "bg-yellow-500",
  COMING_UP: "bg-yellow-500",
  PAUSED: "bg-red-500",
  FAILED: "bg-red-500",
};

export interface EnvironmentFact {
  dotClass: string;
  label: string;
}

/** The two facts under a row's name: its database, then the chatbot chosen for it. */
export function environmentFacts(env: Environment): EnvironmentFact[] {
  return [
    {
      dotClass: STATUS_DOT_CLASS[env.branch.status] || "bg-gray-500",
      label: `database ${env.branch.status}`,
    },
    { dotClass: "bg-green-500", label: `chatbot ${chatbotHostLabel(env.chatbot.url)}` },
  ];
}

/**
 * The row the picker's controls act on: the one last clicked, else the database the app
 * is already on, else the first.
 *
 * Derived from the current list on every render rather than copied into state, so a
 * refresh that drops a branch cannot leave the controls pointing at a row that is gone.
 */
export function selectedDatabase(
  databases: DatabaseTarget[],
  key: string | null,
  appRef: string | null
): DatabaseTarget | null {
  return (
    databases.find((database) => database.key === key) ??
    databases.find((database) => database.ref !== null && database.ref === appRef) ??
    databases[0] ??
    null
  );
}

/**
 * The chatbot the picker's controls will apply: the one chosen, else the one the app is
 * already talking to, else develop.
 *
 * The URL is matched against the offered list rather than trusted, so neither a
 * hand-written override nor a choice left over from a chatbot that has since gone can
 * put a host outside that list into a pair.
 */
export function selectedChatbot(
  chatbots: ChatbotTarget[],
  chosenUrl: string | null
): ChatbotTarget {
  const wanted = normalizeChatbotUrl(chosenUrl ?? resolveChatbotBaseUrl() ?? "");
  return chatbots.find((chatbot) => chatbot.url === wanted) ?? DEVELOP_CHATBOT;
}

/** How a chatbot names itself in the picker's selector. */
export function chatbotLabel(chatbot: ChatbotTarget): string {
  return `${chatbot.name} · ${chatbotHostLabel(chatbot.url)}`;
}
